/**
 * 루미나 화면 층 신작 — 블랙 components/PopupManager.jsx 의 로직 그대로, 스타일 객체·className 만 교체(시안 40-overlays 사이트 팝업 pu-).
 * WHY: SPEC §2 — 조회(active·auto-close-settings)·5분 재확인·위치/크기 계산·자동 닫기·닫기 기록(dismiss)·링크 클릭(click)은 원본 그대로.
 *   · 관리자가 지정한 색(배경·글자·머리 배경·제목 색)·크기·위치값은 인라인으로 그대로 우선하고, 지정이 없을 때의 기본 색·라운드·글꼴만 루미나 규격(CSS)로 넘긴다.
 *   · 머리 줄·닫기 버튼(기본/minimal/rounded)·"N시간 동안 보지 않기" 버튼은 시안 .pu-* 모양. 이모지·문자 × 대신 인라인 SVG.
 *   · 창들은 .pu-layer(클릭 통과 층) 안에 두어 시안 z 순서(팝업 92 < 쪽지 모달 < 게임 리스트 팝업)를 따른다. 창끼리의 겹침 순서(zIndex 1500+index)는 층 안에서 그대로.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DOMPurify from 'dompurify'; // XSS 방지를 위한 HTML 정제 라이브러리
import config from '../config';
import './PopupManager.css';

// 로고 URL을 절대 경로로 변환하는 헬퍼 함수
const getAbsoluteUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  // API 서버 URL 사용
  const apiBaseUrl = config.API_URL || `https://${window.location.hostname}/api`;
  const baseUrl = apiBaseUrl.replace('/api', '');
  return `${baseUrl}${url}`;
};

// 닫기 아이콘(선 색·두께는 CSS .pu-x svg 가 정한다)
const IcoX = () => (<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>);

const PopupManager = () => {
  const [popups, setPopups] = useState([]);
  const [autoCloseSettings, setAutoCloseSettings] = useState(null);
  const [timers, setTimers] = useState({});
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [viewportSize, setViewportSize] = useState({ width: window.innerWidth, height: window.innerHeight });
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // API URL
  const apiUrl = config.API_URL;

  // 함수들을 먼저 정의
  const fetchAutoCloseSettings = async () => {
    try {
      // 유저 API에서 전역 자동 닫기 설정 가져오기
      const response = await axios.get(`${apiUrl}/popups/auto-close-settings`);

      if (response.data.success && response.data.data) {
        setAutoCloseSettings(response.data.data);
        // console.log 제거 - 무한 출력 방지
      }
    } catch (error) {
      // console.log 제거 - 무한 출력 방지
      // 기본값 설정
      setAutoCloseSettings({ close_after_hours: 24, enabled: true });
    }
  };

  const fetchActivePopups = async () => {
    try {
      // 토큰이 있으면 Authorization 헤더에 포함
      const userToken = localStorage.getItem('token');
      // console.log 제거 - 무한 출력 방지

      const response = await axios.get(`${apiUrl}/popups/active?page=user`, {
        headers: {
          'Authorization': userToken ? `Bearer ${userToken}` : undefined
        }
      });

      if (response.data.success) {
        // console.log 제거 - 무한 출력 방지

        // DB에서 이미 필터링된 팝업만 받아옴 (localStorage 필터링 제거)
        // 서버가 popup_dismissals 테이블을 확인해서
        // 현재 유저가 숨기지 않은 팝업만 전송함
        setPopups(response.data.data);
      }
    } catch (error) {
      console.error('PopupManager: 팝업 로드 오류:', error);
    }
  };

  // 토큰 상태 체크 및 이벤트 리스너
  useEffect(() => {
    // 토큰 체크 함수
    const checkAuth = () => {
      const token = localStorage.getItem('token');  // 'userToken'이 아니라 'token' 사용
      setIsAuthenticated(!!token);

      if (token) {
        // 전역 자동 닫기 설정 가져오기
        fetchAutoCloseSettings();

        // 활성 팝업 가져오기 (DB 기반 필터링)
        fetchActivePopups();
      } else {
        // 토큰이 없으면 팝업 초기화
        setPopups([]);
      }
    };

    // 초기 체크
    checkAuth();

    // 로그인/로그아웃 상태 변화 감지
    const handleStorageChange = (event) => {
      if (event.key === 'token') {  // 'userToken'이 아니라 'token' 사용
        // console.log 제거 - 무한 출력 방지
        checkAuth();
      }
    };

    // 로그인/로그아웃 커스텀 이벤트 감지 (authStateChanged 단일 이벤트명으로 통일)
    const handleAuthChange = () => {
      // console.log 제거 - 무한 출력 방지
      checkAuth();
    };

    // 이벤트 리스너 등록
    // authChange는 authStateChanged와 중복이므로 제거 - authStateChanged만 사용
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('authStateChanged', handleAuthChange);

    // 5분마다 만료된 팝업 체크 (시간 기반 닫기 만료 확인, 과도한 API 요청 방지)
    const intervalId = setInterval(() => {
      const token = localStorage.getItem('token');
      if (token) {
        // console.log 제거 - 무한 출력 방지
        fetchActivePopups();
      }
    }, 300000); // 300초(5분)마다 체크

    // 컴포넌트 언마운트 시 이벤트 리스너 제거
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('authStateChanged', handleAuthChange);
      clearInterval(intervalId);
    };
  }, []);  // 의존성 배열 비워서 무한 루프 방지

  // 컴포넌트 언마운트 시 모든 타이머 정리
  useEffect(() => {
    return () => {
      Object.values(timers).forEach(timer => clearTimeout(timer));
    };
  }, [timers]);

  // 뷰포트 크기 변화 감지
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      setViewportSize({ width, height });
      setIsMobile(width <= 768);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  // 자동 닫기 타이머 설정
  useEffect(() => {
    if (popups && popups.length > 0) {
      popups.forEach(popup => {
        if (popup.auto_close_seconds && popup.auto_close_seconds > 0) {
          const timer = setTimeout(() => {
            handlePopupClose(popup);
          }, popup.auto_close_seconds * 1000);

          setTimers(prev => ({
            ...prev,
            [popup.id]: timer
          }));
        }
      });
    }
  }, [popups]);

  // 모든 Hook 호출 후 인증 체크
  if (!isAuthenticated) {
    return null;
  }

  const trackPopupView = async (popupId) => {
    try {
      const userToken = localStorage.getItem('token');
      await axios.post(`${apiUrl}/popups/${popupId}/view`, {}, {
        headers: userToken ? {
          'Authorization': `Bearer ${userToken}`
        } : {}
      });
    } catch (error) {
      console.error('팝업 뷰 추적 오류:', error);
    }
  };

  const trackPopupClick = async (popupId) => {
    try {
      const userToken = localStorage.getItem('token');
      await axios.post(`${apiUrl}/popups/${popupId}/click`, {}, {
        headers: userToken ? {
          'Authorization': `Bearer ${userToken}`
        } : {}
      });
    } catch (error) {
      console.error('팝업 클릭 추적 오류:', error);
    }
  };

  const handlePopupClose = async (popup) => {
    // 타이머 정리
    if (timers[popup.id]) {
      clearTimeout(timers[popup.id]);
      setTimers(prev => {
        const newTimers = { ...prev };
        delete newTimers[popup.id];
        return newTimers;
      });
    }

    // 팝업 닫기 추적 (DB에 dismiss 기록 저장)
    try {
      const userToken = localStorage.getItem('token');
      await axios.post(`${apiUrl}/popups/${popup.id}/dismiss`, {}, {
        headers: userToken ? {
          'Authorization': `Bearer ${userToken}`
        } : {}
      });
    } catch (error) {
      console.error('팝업 닫기 추적 오류:', error);
    }

    // 팝업 목록에서 제거
    setPopups(prev => prev.filter(p => p.id !== popup.id));
  };

  const handleTimeBasedClose = async (popup) => {
    // 타이머 정리
    if (timers[popup.id]) {
      clearTimeout(timers[popup.id]);
      setTimers(prev => {
        const newTimers = { ...prev };
        delete newTimers[popup.id];
        return newTimers;
      });
    }

    // DB에 시간 기반 닫기 정보 저장
    const hours = popup.close_after_hours || 12;
    // console.log 제거 - 무한 출력 방지

    // 시간 기반 닫기 추적 (DB에 expires_at 포함해서 저장)
    try {
      const userToken = localStorage.getItem('token');

      await axios.post(`${apiUrl}/popups/${popup.id}/dismiss`,
        { hours: hours }, // 팝업별 설정의 시간 정보 전달
        {
          headers: userToken ? {
            'Authorization': `Bearer ${userToken}`
          } : {}
        }
      );
    } catch (error) {
      console.error('시간 기반 팝업 닫기 추적 오류:', error);
    }

    // 팝업 목록에서 제거
    setPopups(prev => prev.filter(p => p.id !== popup.id));
  };

  const handlePopupClick = (popup) => {
    trackPopupClick(popup.id);

    if (popup.link_url) {
      window.open(popup.link_url, '_blank');
    }

    if (popup.close_on_click) {
      handlePopupClose(popup);
    }
  };

  // Process popup content to remove problematic BR tags and inline margin styles
  const processPopupContent = (content) => {
    if (!content) return '';
    // Remove trailing BR tags and BR tags immediately after images
    let processed = content
      .replace(/<br\s*\/?\>\s*$/gi, '')  // Remove trailing BR
      .replace(/(<img[^>]*>)\s*<br\s*\/?\>/gi, '$1');  // Remove BR after images

    // Convert relative image URLs to absolute URLs
    processed = processed.replace(/src="(\/uploads\/[^"]+)"/gi, (match, path) => {
      return `src="${getAbsoluteUrl(path)}"`;
    });
    processed = processed.replace(/src='(\/uploads\/[^']+)'/gi, (match, path) => {
      return `src='${getAbsoluteUrl(path)}'`;
    });

    // Remove margin: 0 from img inline styles to allow CSS to take effect
    processed = processed.replace(/(<img[^>]*style=["'][^"']*);\s*margin:\s*0;?([^"']*["'][^>]*>)/gi, '$1$2');
    processed = processed.replace(/(<img[^>]*style=["'][^"']*)margin:\s*0;?\s*([^"']*["'][^>]*>)/gi, '$1$2');

    // Fix Mixed Content issues and update URLs for production
    try {
      const apiUrlObj = new URL(apiUrl);
      const isProduction = window.location.protocol === 'https:';

      if (isProduction) {
        // 프로덕션 환경 - 모든 URL을 HTTPS로 변환
        processed = processed.replace(/http:\/\//g, 'https://');

        // 개발 서버 IP가 DB에 저장된 경우 현재 호스트명으로 치환
        // 주의: 아래 IP는 개발/업로드 서버 IP임. 서버가 변경되면 이 목록도 수정 필요
        processed = processed.replace(/220\.95\.232\.167/g, window.location.hostname);
        processed = processed.replace(/125\.187\.89\.85/g, window.location.hostname);

        // 프로덕션에서는 리버스 프록시를 사용하므로 포트 번호 제거
        processed = processed.replace(/:5100\//g, '/');
        processed = processed.replace(/:5000\//g, '/');
        processed = processed.replace(/:5101\//g, '/');
      } else {
        // 개발 환경 - 업로드 서버 포트(5100) 유지, API 포트(5000)만 5101로 통일
        processed = processed.replace(/:5000\//g, ':5101/');
      }
    } catch (error) {
      // URL 파싱 실패 시 폴백: 프로토콜 기준으로 처리
      if (window.location.protocol === 'https:') {
        processed = processed.replace(/http:\/\//g, 'https://');
        // 개발 서버 IP를 현재 호스트명으로 치환 (서버 IP 변경 시 수정 필요)
        processed = processed.replace(/220\.95\.232\.167/g, window.location.hostname);
        processed = processed.replace(/125\.187\.89\.85/g, window.location.hostname);
        // 리버스 프록시 사용 환경에서 포트 제거
        processed = processed.replace(/:5100\//g, '/');
        processed = processed.replace(/:5000\//g, '/');
        processed = processed.replace(/:5101\//g, '/');
      } else {
        // 개발 환경: API 포트만 5101로 통일
        processed = processed.replace(/:5000\//g, ':5101/');
      }
    }

    return processed;
  };

  // 반응형 크기 계산 - 일관성 있는 크기 조정
  const calculateSize = (originalWidth, originalHeight, index = 0) => {
    // 원본 크기 저장 (관리자 설정값) - 'auto'를 기본값으로 처리
    const baseWidth = originalWidth === 'auto' ? 600 : Number(originalWidth) || 600;
    const baseHeight = originalHeight === 'auto' ? 800 : Number(originalHeight) || 800;

    // 모바일 처리
    if (isMobile) {
      const maxWidth = viewportSize.width * 0.9;
      const maxHeight = viewportSize.height * 0.85;

      let mobileScale = 1;
      if (baseWidth > maxWidth) {
        mobileScale = maxWidth / baseWidth;
      }
      if (baseHeight * mobileScale > maxHeight) {
        mobileScale = Math.min(mobileScale, maxHeight / baseHeight);
      }

      return {
        width: Math.floor(baseWidth * mobileScale),
        height: Math.floor(baseHeight * mobileScale)
      };
    }

    // 데스크톱 처리 - 뷰포트에 완전히 반응
    const totalPopups = popups.length;
    const cascadeSpace = totalPopups > 1 ? (totalPopups - 1) * 30 : 0;
    const padding = 60; // 좌우 여백
    const verticalPadding = 60; // 상하 여백 (위아래 각 30px)

    // 사용 가능한 공간 계산
    const availableWidth = viewportSize.width - padding - cascadeSpace;
    const availableHeight = viewportSize.height - verticalPadding - cascadeSpace;

    // 모든 팝업 중 최대 크기 찾기
    let maxWidth = baseWidth;
    let maxHeight = baseHeight;

    if (popups.length > 0) {
      popups.forEach(p => {
        if (p.width > maxWidth) maxWidth = p.width;
        if (p.height > maxHeight) maxHeight = p.height;
      });
    }

    // 축소 비율 계산 - 뷰포트에 맞게 자동 조정
    let scaleX = 1;
    let scaleY = 1;

    // 가로가 화면을 벗어나면 축소 비율 계산
    if (maxWidth > availableWidth) {
      scaleX = availableWidth / maxWidth;
    }

    // 세로가 화면을 벗어나면 축소 비율 계산
    if (maxHeight > availableHeight) {
      scaleY = availableHeight / maxHeight;
    }

    // 가로/세로 중 더 작은 비율 사용 (비율 유지)
    const scale = Math.max(Math.min(scaleX, scaleY), 0.3);

    // 모든 팝업에 동일한 비율 적용
    const finalWidth = Math.floor(baseWidth * scale);
    const finalHeight = Math.floor(baseHeight * scale);

    return {
      width: finalWidth,
      height: finalHeight
    };
  };

  const getPopupStyle = (popup, index) => {
    const { width, height } = calculateSize(popup.width, popup.height, index);

    // 디버깅용 로그 - 세로 문제 진단
    console.log(`[Popup ${index}] Height Analysis:`, {
      viewportHeight: viewportSize.height,
      originalHeight: popup.height,
      calculatedHeight: height,
      maxHeightLimit: viewportSize.height - 60,
      willOverflow: height > (viewportSize.height - 60),
      needsScroll: popup.height > (viewportSize.height - 60),
      isMobile,
      appliedStyle: `height: ${height}px, maxHeight: calc(100vh - 60px)`
    });

    // 기본 색·라운드·그림자·글꼴 크기는 루미나 CSS(.pu-win .pu-s-*)가 정한다 — 관리자가 지정한 배경·글자 색만 인라인으로 우선
    const baseStyle = {
      width: `${width}px`,
      height: `${height}px`,
      maxHeight: `calc(100vh - 60px)`, // 뷰포트 높이 제한
      position: 'fixed',
      zIndex: 1500 + index,
      ...(popup.background_color ? { backgroundColor: popup.background_color } : {}),
      ...(popup.text_color ? { color: popup.text_color } : {}),
      overflowY: 'auto', // 세로 스크롤 활성화
      overflowX: 'hidden',
      display: 'flex',
      flexDirection: 'column'
    };

    // 모바일에서는 중앙에 표시
    if (isMobile) {
      // 스택 효과를 위한 오프셋 (더 작게)
      const mobileOffset = index * 8;

      return {
        ...baseStyle,
        position: 'fixed',
        top: `calc(50% + ${mobileOffset}px)`,
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: `${width}px`,
        maxWidth: `calc(100vw - 20px)`,
        height: `${height}px`,
        maxHeight: 'calc(100vh - 40px)', // 모바일에서도 뷰포트 높이 제한
        minWidth: '280px',
        minHeight: '200px'
      };
    }

    // 정밀한 위치가 설정되어 있으면 우선 사용 (0이 아닌 값만)
    if ((popup.top_position !== null && popup.top_position !== undefined && popup.top_position !== 0) ||
        (popup.left_position !== null && popup.left_position !== undefined && popup.left_position !== 0)) {
      // 화면 밖으로 나가지 않도록 조정
      let top = popup.top_position;
      let left = popup.left_position;

      if (left + width > viewportSize.width) {
        left = viewportSize.width - width - 20;
      }
      if (top + height > viewportSize.height) {
        top = viewportSize.height - height - 20;
      }

      return {
        ...baseStyle,
        top: `${Math.max(20, top)}px`,
        left: `${Math.max(20, left)}px`
      };
    }

    // 위치에 따른 스타일 설정
    switch (popup.position) {
      case 'center':
        // 중앙 정렬 - 크기 조정 우선, 캐스케이드 오프셋 최소화
        const offsetX = index * 10; // 매우 작은 오프셋
        const offsetY = index * 10;

        return {
          ...baseStyle,
          top: '50%',
          left: '50%',
          transform: `translate(calc(-50% + ${offsetX}px), calc(-50% + ${offsetY}px))`,
          width: `${width}px`,
          height: `${height}px`
        };
      case 'top-left':
        return {
          ...baseStyle,
          top: `${20 + (index * 15)}px`,
          left: `${20 + (index * 15)}px`,
          width: `${width}px`,
          height: `${height}px`,
          maxWidth: `${width}px`,
          maxHeight: `${height}px`
        };
      case 'top-right':
        return {
          ...baseStyle,
          top: `${20 + (index * 15)}px`,
          right: `${20 + (index * 15)}px`,
          width: `${width}px`,
          height: `${height}px`,
          maxWidth: `${width}px`,
          maxHeight: `${height}px`
        };
      case 'bottom-left':
        return {
          ...baseStyle,
          bottom: `${20 + (index * 15)}px`,
          left: `${20 + (index * 15)}px`,
          width: `${width}px`,
          height: `${height}px`,
          maxWidth: `${width}px`,
          maxHeight: `${height}px`
        };
      case 'bottom-right':
        return {
          ...baseStyle,
          bottom: `${20 + (index * 15)}px`,
          right: `${20 + (index * 15)}px`,
          width: `${width}px`,
          height: `${height}px`,
          maxWidth: `${width}px`,
          maxHeight: `${height}px`
        };
      default:
        return baseStyle;
    }
  };

  // 닫기 버튼 모양(관리자 설정 close_button_style: default/minimal/rounded) — 인라인 스타일 객체 대신 시안 .pu-x 변형 클래스
  const closeButtonClass = (style) => `pu-x${style && style !== 'default' ? ` pu-x-${style}` : ''}`;

  return (
    <div className="pu-layer">
      {popups.map((popup, index) => (
        <div
          key={popup.id}
          className={`pu-win pu-t-${popup.popup_type || 'notice'} ${
            popup.show_header === false ? 'pu-noh' : ''
          } pu-s-${popup.text_size || 'medium'} ${isMobile ? 'pu-mobile' : 'pu-desktop'}`}
          role="dialog"
          aria-label={popup.title || '팝업'}
          style={getPopupStyle(popup, index)}
        >
          {popup.show_header !== false && (
            <div
              className="pu-h"
              style={popup.header_background_color ? { backgroundColor: popup.header_background_color } : undefined}
            >
              <h3 style={popup.title_color ? { color: popup.title_color } : undefined}>{popup.title}</h3>
              <button
                type="button"
                className={closeButtonClass(popup.close_button_style)}
                onClick={() => handlePopupClose(popup)}
                aria-label="닫기"
              >
                <IcoX />
              </button>
            </div>
          )}

          {popup.show_header === false && (
            <button
              type="button"
              className={closeButtonClass(popup.close_button_style)}
              onClick={() => handlePopupClose(popup)}
              aria-label="닫기"
            >
              <IcoX />
            </button>
          )}

          {/* DOMPurify로 HTML 정제 후 렌더링 - XSS 방지 */}
          <div
            className={`pu-b${popup.link_url ? ' pu-link' : ''}`}
            onClick={() => handlePopupClick(popup)}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(processPopupContent(popup.content)) }}
          />

          {/* 시간 기반 닫기 버튼 - 각 팝업의 개별 설정 사용 */}
          {popup.close_after_hours && popup.close_after_hours > 0 && (
            <div className="pu-f">
              <button
                type="button"
                className="lk-btn ghost sm"
                onClick={() => handleTimeBasedClose(popup)}
              >
                {popup.close_after_hours}시간 동안 보지 않기
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default PopupManager;
